import { z } from 'zod'

/**
 * Transform Value Object
 * Represents transformation properties
 */
export const TransformSchema = z.object({
  rotation: z.number().default(0), // in degrees
  scaleX: z.number().default(1),
  scaleY: z.number().default(1),
  skewX: z.number().default(0),
  skewY: z.number().default(0),
})

export type Transform = z.infer<typeof TransformSchema>

// Factory functions
export const createTransform = (params?: Partial<Transform>): Transform =>
  TransformSchema.parse(params || {})

export const identityTransform = (): Transform => createTransform()

// Domain operations
export const transformOperations = {
  rotate: (transform: Transform, degrees: number): Transform => ({
    ...transform,
    rotation: (transform.rotation + degrees) % 360,
  }),

  scale: (transform: Transform, scaleX: number, scaleY?: number): Transform => ({
    ...transform,
    scaleX: transform.scaleX * scaleX,
    scaleY: transform.scaleY * (scaleY ?? scaleX),
  }),

  flipHorizontal: (transform: Transform): Transform => ({
    ...transform,
    scaleX: -transform.scaleX,
  }),

  flipVertical: (transform: Transform): Transform => ({
    ...transform,
    scaleY: -transform.scaleY,
  }),

  reset: (): Transform => identityTransform(),

  toMatrix: (transform: Transform): string => {
    // Convert to SVG transform matrix string
    const { rotation, scaleX, scaleY, skewX, skewY } = transform
    const rad = (rotation * Math.PI) / 180
    const cos = Math.cos(rad)
    const sin = Math.sin(rad)

    // Apply transformations in order: scale, skew, rotate
    const a = scaleX * cos - skewY * sin
    const b = scaleX * sin + skewY * cos
    const c = skewX * cos - scaleY * sin
    const d = skewX * sin + scaleY * cos

    return `matrix(${a} ${b} ${c} ${d} 0 0)`
  },

  toCSSString: (transform: Transform): string => {
    // Convert to CSS transform string (excluding rotation - stored separately)
    const { scaleX, scaleY, skewX, skewY } = transform
    const transforms: string[] = []

    if (scaleX !== 1 || scaleY !== 1) {
      if (scaleX === scaleY) {
        transforms.push(`scale(${scaleX})`)
      } else {
        transforms.push(`scale(${scaleX}, ${scaleY})`)
      }
    }

    if (skewX !== 0) {
      transforms.push(`skewX(${skewX}deg)`)
    }

    if (skewY !== 0) {
      transforms.push(`skewY(${skewY}deg)`)
    }

    return transforms.length > 0 ? transforms.join(' ') : ''
  },

  fromCSSString: (cssString: string): Transform => {
    // Parse CSS transform string back to transform object
    // Note: rotation is now stored separately, but we still parse it for backward compatibility
    const transform: Transform = {
      rotation: 0,
      scaleX: 1,
      scaleY: 1,
      skewX: 0,
      skewY: 0,
    }

    if (!cssString) return transform

    // Parse rotate (for backward compatibility with old saves)
    const rotateMatch = cssString.match(/rotate\((-?\d+(?:\.\d+)?)deg\)/)
    if (rotateMatch) {
      transform.rotation = parseFloat(rotateMatch[1])
    }

    // Parse scale
    const scaleMatch = cssString.match(/scale\((-?\d+(?:\.\d+)?)\s*(?:,\s*(-?\d+(?:\.\d+)?))?\)/)
    if (scaleMatch) {
      transform.scaleX = parseFloat(scaleMatch[1])
      transform.scaleY = scaleMatch[2] ? parseFloat(scaleMatch[2]) : parseFloat(scaleMatch[1])
    }

    // Parse skewX
    const skewXMatch = cssString.match(/skewX\((-?\d+(?:\.\d+)?)deg\)/)
    if (skewXMatch) {
      transform.skewX = parseFloat(skewXMatch[1])
    }

    // Parse skewY
    const skewYMatch = cssString.match(/skewY\((-?\d+(?:\.\d+)?)deg\)/)
    if (skewYMatch) {
      transform.skewY = parseFloat(skewYMatch[1])
    }

    return transform
  },
}
